import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'
import {
  createSearchParams,
  useLocation,
  useNavigate,
  useSearchParams,
  type NavigateOptions,
} from 'react-router-dom'
import { debounce, isEqual } from 'lodash'
import { DateTime } from 'luxon'
import {
  FlagQuery,
  FlagValue,
  JsonValue,
  ProviderStatus,
  useFlag,
  useOpenFeatureClientStatus,
} from '@openfeature/react-sdk'

// We need the base useSelector in here for helpers so it's fine to import
// eslint-disable-next-line no-restricted-imports
import { useSelector } from 'react-redux'

import {
  getDefaultSidebarState,
  setScrollYState as setReduxSidebarScrollYState,
  setSidebarState as setReduxSidebarState,
  SidebarConfigProps,
  toggleCategory as toggleCategoryOpenClose,
  toggleSidebar as toggleSidebarOpenClose,
} from '../reducers/sidebar.slice'
import { getSidebarState } from '../selectors/sidebarSelectors'
import { Dispatch, GetState, ReduxState, useAppDispatch } from './typeHelpers'
import { selectFlyoutState } from '../selectors/selectFlyout'
import { closeFlyoutV2, openFlyoutV2 } from '../reducers/flyout.slice'
import { useAnalyticsTrack } from '@/features/amplitude'
import {
  FinancialInsightsButtonType,
  INSIGHTS_TYPE,
  insightsCopyConstants,
} from '@/features/financial-insights/shared/utils'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'

export const usePrevious = <T>(value: T): T | undefined => {
  const ref = useRef<T>()
  useEffect(() => {
    ref.current = value
  }, [value])
  return ref.current
}

// This works like a Component's componentDidMount.
// Essentially it skips any component changes and only will run when the component is first mounted
// It should rarely be used because it breaks the rules/concept of hooks
export const useMountEffect = (fn: () => void) => {
  useEffect(() => {
    fn()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}

// Creates a timeout ref that will automatically cancel if the component is unmounted
export const useTimeoutRef = () => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  return timeoutRef
}

export const useIntervalRef = () => {
  const intervalRef = useRef<ReturnType<typeof setInterval>>()

  useEffect(() => {
    const currentInterval = intervalRef.current
    return () => {
      if (currentInterval) {
        clearInterval(currentInterval)
      }
    }
  }, [])

  return intervalRef
}

let windowSize = { width: window.innerWidth, height: window.innerHeight }
const sizeListeners = new Set<() => void>()

// Snapshot re-reads live: device emulation changes the viewport without firing
// `resize`, so returning the last event's value reports a desktop width on a
// phone. Replaced only when it moved, so React sees a stable reference.
const getWindowSize = () => {
  if (
    windowSize.width !== window.innerWidth ||
    windowSize.height !== window.innerHeight
  ) {
    windowSize = { width: window.innerWidth, height: window.innerHeight }
  }
  return windowSize
}

const subscribeToWindowSize = (listener: () => void) => {
  sizeListeners.add(listener)
  return () => {
    sizeListeners.delete(listener)
  }
}

window.addEventListener(
  'resize',
  debounce(() => sizeListeners.forEach((l) => l()), 100)
)

export const useWindowSize = () =>
  useSyncExternalStore(subscribeToWindowSize, getWindowSize)

const flyoutContentRef = { current: null as React.ReactNode | null }
const preventCloseRef = { current: null as (() => boolean) | null }

/**
 * This hook is used to open and close the flyout for Design System 2
 */
export const useFlyoutV2 = () => {
  const dispatch = useAppDispatch()
  const { open, direction, title, titleIcon, footer, description } =
    useSelector(selectFlyoutState)

  const openFlyout = useCallback(
    ({
      content,
      preventClose,
      ...rest
    }: {
      content: React.ReactNode
      preventClose?: () => boolean
      title?: string
      direction?: 'right' | 'left' | 'top' | 'bottom'
      titleIcon?: IconDefinition
      footer?: React.ReactNode
      description?: string
    }) => {
      flyoutContentRef.current = content
      preventCloseRef.current = preventClose ?? null
      dispatch(openFlyoutV2(rest))
    },
    [dispatch]
  )

  const closeFlyout = useCallback(() => {
    if (preventCloseRef.current?.()) return
    dispatch(closeFlyoutV2())
    flyoutContentRef.current = null
    preventCloseRef.current = null
  }, [dispatch])

  return {
    open,
    direction,
    title,
    titleIcon,
    footer,
    description,
    openFlyout,
    closeFlyout,
    getContent: () => flyoutContentRef.current,
    getPreventClose: () => preventCloseRef.current?.(),
  }
}
export const useSidebar = () => {
  const dispatch = useAppDispatch()
  const { open, sidebarState, scrollYPos } = useSelector(getSidebarState())
  const { pathname } = useLocation()

  const setSidebarState = useCallback(
    (configProps: Record<string, SidebarConfigProps>, defaultOpen = false) => {
      dispatch(
        setReduxSidebarState(
          getDefaultSidebarState(configProps, pathname, defaultOpen)
        )
      )
    },
    [dispatch, pathname]
  )

  const toggleCategory = useCallback(
    (key: string) => {
      dispatch(toggleCategoryOpenClose(key))
    },
    [dispatch]
  )

  const setScrollYState = useCallback(
    (yPos: number) => {
      dispatch(setReduxSidebarScrollYState(yPos))
    },
    [dispatch]
  )

  const toggleSidebar = useCallback(
    (newOpen?: boolean) => {
      dispatch(toggleSidebarOpenClose(newOpen))
    },
    [dispatch]
  )

  return {
    open,
    setSidebarState,
    sidebarState,
    toggleCategory,
    toggleSidebar,
    setScrollYState,
    scrollYPos,
  }
}

export const useReselector = <Args extends unknown[], R>(
  selector: (state: ReduxState, ...params: Args) => R,
  ...params: Args
) => useSelector((state: ReduxState) => selector(state, ...params))

// scrollInnerOnly will only scroll the inner scroll.  If it is false the entire page will scroll
export const useScrollRef = ({
  scrollInnerOnly = false,
  autoScroll = false,
}: { scrollInnerOnly?: boolean; autoScroll?: boolean } = {}) => {
  const scrollRef = useRef<HTMLSpanElement>(null)

  const scrollToRef = useCallback(
    () =>
      scrollRef.current?.scrollIntoView({
        behavior: autoScroll ? 'auto' : 'smooth',
        ...(scrollInnerOnly && { block: 'nearest', inline: 'start' }),
      }),
    [autoScroll, scrollInnerOnly]
  )

  return {
    scrollToRef,
    scrollRef,
  }
}

// Scrolls to a hash target in the URL once async content is ready
export const useHashScroll = (ready: boolean) => {
  const location = useLocation()

  useEffect(() => {
    if (ready && location.hash) {
      document
        .querySelector(location.hash)
        ?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [ready, location.hash])
}

export const useNavigateWithPersistParams = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  return (
    to: string,
    newParams?: { [key: string]: string },
    options?: NavigateOptions
  ) => {
    const params = Object.entries(newParams || {})

    for (const entry of searchParams.entries()) {
      params.push(entry)
    }

    return params.length
      ? navigate(`${to}?${createSearchParams(params)}`, options)
      : navigate(to, options)
  }
}

export const useToggle = (initialState = false): [boolean, () => void] => {
  const [state, setState] = useState(initialState)

  const toggle = useCallback(() => {
    setState((prev) => !prev)
  }, [])

  return [state, toggle]
}

// Used as shortcut command for api calls when a component needs the response but it's not saved in the redux state
// Usage `const res = useFetchResponse(fetchCall, 'fetchParam', 'secondParam')`
// Note - This will NOT work if a function is a parameter.  `JSON.stringify` will not pick up those changes
export const useFetchResponseWithUtils = <T, Args extends unknown[]>(
  apiCall: (...params: Args) => (_: Dispatch, getState: GetState) => Promise<T>,
  ...params: Args
) => {
  const dispatch = useAppDispatch()
  const [result, setResult] = useState<T>()
  const [loading, setLoading] = useState(true)
  const paramString = JSON.stringify(params)

  const fetch = useCallback(async () => {
    setResult(await dispatch(apiCall(...params)))
    setLoading(false)
    // If any of the params change you want to refetch but if params are sent in dependencies the array comparison will
    // never work.  Instead the params are stringified and sent in which works but eslint will complain
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiCall, dispatch, paramString])

  useEffect(() => {
    fetch()
  }, [fetch])

  return { result, refetch: fetch, loading }
}

export const useFetchResponse = <T, Args extends unknown[]>(
  apiCall: (...params: Args) => (_: Dispatch, getState: GetState) => Promise<T>,
  ...params: Args
) => {
  const { result } = useFetchResponseWithUtils(apiCall, ...params)

  return result
}

export const useMemoizedValue = <T>(initVal: T) => {
  const [val, setVal] = useState(initVal)

  useEffect(() => {
    if (!isEqual(val, initVal)) {
      setVal(initVal)
    }
  }, [initVal, val])

  return val
}

export const useAsyncCallback = <T, A extends unknown[]>(
  sentCallback: (...args: A) => Promise<T>,
  throwError = true
) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | undefined>()
  const [result, setResult] = useState<T>()

  const callback = useCallback(
    async (...args: A) => {
      setLoading(true)
      try {
        setResult(await sentCallback(...args))
      } catch (err: unknown) {
        const caughtError =
          err instanceof Error
            ? err
            : typeof err === 'string'
              ? new Error(err)
              : new Error('Unknown error')

        setError(caughtError)

        if (throwError) {
          setLoading(false)
          throw caughtError
        }
      } finally {
        setLoading(false)
      }
    },
    [sentCallback, throwError]
  )

  return { loading, result, error, callback }
}

// Once triggerValue is true, will set value to sentValue and will not change again.
// This can be helpful for things like lists that load off of redux but don't change once redux is edited
export const useTriggerSet = <T>(sentValue: T, triggerValue: boolean) => {
  const [value, setValue] = useState(sentValue)
  const [hasTriggered, setHasTriggered] = useState(false)

  useEffect(() => {
    if (triggerValue && !hasTriggered) {
      setHasTriggered(true)
      setValue(sentValue)
    }
  }, [hasTriggered, sentValue, triggerValue])

  return value
}

/*
  Get end date logic example: 
  Today is February 5, 2024
  Insights shown through January 31, 2024
  Today is February 1, 2024
  Insights shown through December 31, 2023
  Today is January 11, 2024
  Insights shown through December 31, 2023
  Today is January 04, 2024
  Insights shown through November 30, 2023
*/
const releaseDayOfMonth = 5

export const useEndDateForFinancialInsights = (currentDate: DateTime) =>
  useMemo(
    () =>
      currentDate
        .minus({ months: currentDate.day >= releaseDayOfMonth ? 1 : 2 })
        .endOf('month'),
    [currentDate]
  )

export const useTrackFinancialInsightsButtonClick = () => {
  const track = useAnalyticsTrack()

  const trackInsights = ({
    button,
    insightsType,
  }: {
    button: FinancialInsightsButtonType
    insightsType: INSIGHTS_TYPE
  }) => {
    track(insightsCopyConstants[insightsType].trackButton, { button })
  }

  return trackInsights
}

/**
 * Hook that returns a function that returns whether the component is mounted.
 * @returns true or false
 */
export const useMountedState = (): (() => boolean) => {
  const mountedRef = useRef<boolean>(false)
  const get = useCallback(() => mountedRef.current, [])

  useEffect(() => {
    mountedRef.current = true

    return () => {
      mountedRef.current = false
    }
  }, [])

  return get
}

/**
 * Hook that runs an async function on mount and returns the state.
 * It's a shortcut for useAsyncCallback where the function is called on mount.
 *
 * @param fn an async function
 * @returns the state of the async function and the function itself
 */
export const useAsync = <T>(fn: () => Promise<T>) => {
  const state = useAsyncCallback(fn, false)

  useEffect(() => {
    state.callback()
  }, [state.callback]) // eslint-disable-line react-hooks/exhaustive-deps
  // eslint wants state to be in the dependencies but it only depends on the callback

  return { loading: state.loading, result: state.result, error: state.error }
}

/**
 * True while the OpenFeature provider has not produced an authoritative
 * evaluation for the current context — either it has not yet initialized
 * (NOT_READY) or it is reconciling after a context change (RECONCILING).
 * During RECONCILING the LaunchDarkly provider returns the supplied default
 * value rather than the previously-evaluated value, which is what
 * `useLoadedFlagValue` callers must avoid acting on.
 */
export const isOpenFeatureProviderLoading = (status: ProviderStatus): boolean =>
  status === ProviderStatus.NOT_READY || status === ProviderStatus.RECONCILING

/**
 * Hook that returns the value of a flag, but returns null if the flag context is not yet loaded
 */
export const useLoadedFlagValue = <T extends FlagValue>(
  flagName: string,
  defaultVal: T
) => {
  const status = useOpenFeatureClientStatus()
  const flag: FlagQuery<
    T extends boolean
      ? boolean | null
      : T extends number
        ? number | null
        : T extends string
          ? string | null
          : T extends JsonValue
            ? T | null
            : JsonValue | null
  > = useFlag(flagName, defaultVal, {
    hooks: [
      {
        after(hookContext, evaluationDetails, _hookHints) {
          if (!hookContext.context.targetingKey) {
            evaluationDetails.value = null
          }
        },
      },
    ],
  })

  if (isOpenFeatureProviderLoading(status)) {
    return null
  }
  return flag.value
}

/**
 * Hook that debounces setting a value
 * @param value T
 * @param delay debounce delay in millis
 * @returns T (but debounced)
 */
export const useDebounce = <T>(value: T, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}

/**
 * Reads a boolean query flag once, then strips it from the URL so a refresh or
 * back-navigation doesn't re-trigger it. Returns whether it was present at mount
 * (for render-driven use); pass onConsume for side-effect use.
 */
export const useConsumeQueryFlag = (
  param: string,
  onConsume?: () => void
): boolean => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [wasPresent] = useState(() => searchParams.get(param) === 'true')
  const onConsumeRef = useRef(onConsume)
  onConsumeRef.current = onConsume

  useEffect(() => {
    if (searchParams.get(param) !== 'true') {
      return
    }
    onConsumeRef.current?.()
    const next = new URLSearchParams(searchParams)
    next.delete(param)
    setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams, param])

  return wasPresent
}
